import { Map as ImmutableMap, type Map as ImmutableMapType } from 'immutable';
import { DateTime } from 'luxon';
import numeral from 'numeral';
import { createSelector } from 'reselect';

import { type ACCOUNTS_REDUCER_KEY, accountsSelectors, type accountsTypes } from 'app-components/flux/accounts';
import { balanceSelectors } from 'app-components/flux/calculated-balances';
import { type DATE_REDUCER_KEY, dateSelectors } from 'app-components/flux/date';
import { type FEATURE_FLAGS_REDUCER_KEY, featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  type TRANSACTIONS_REDUCER_KEY,
  transactionsSelectors,
  type transactionsTypes,
} from 'app-components/flux/transactions';
import { TransactionSubType } from 'app-components/flux/transactions/transactionsTypes';
import { EPOCH_ISO } from 'app-components/utils/date';
import { numberUtils } from 'app-components/utils/number';
import type RootState from 'app-components/utils/redux-store/rootState';

import { GOALS_REDUCER_KEY, type Goal, mkGoalFundingAccount } from './goalsTypes';

type State = Pick<
  RootState,
  | typeof TRANSACTIONS_REDUCER_KEY
  | typeof ACCOUNTS_REDUCER_KEY
  | typeof FEATURE_FLAGS_REDUCER_KEY
  | typeof GOALS_REDUCER_KEY
  | typeof DATE_REDUCER_KEY
>;

type GoalTagProps = { id?: string };
type GoalTxnDateProps = { acctId: string; month: number; year: number };
type GoalAccountProps = { accountId?: string };
type GoalTxnsById = ImmutableMapType<string, transactionsTypes.TransactionRecord>;
type AccountsById = ImmutableMapType<string, accountsTypes.Account>;
const emptyGoalTransactions: GoalTxnsById = ImmutableMap<string, transactionsTypes.TransactionRecord>();

export const getGoalsStore = (state: State) => state[GOALS_REDUCER_KEY];
export const getResourcesById = (state: State): ImmutableMapType<string, Goal> =>
  getGoalsStore(state).resourcesById as ImmutableMapType<string, Goal>;

export const getGoalById = (state: State, id: string): Goal | undefined => getResourcesById(state).get(id);

export const getIsLoading = (state: State) => getGoalsStore(state).isLoading;
export const getLoadPending = (state: State) => getGoalsStore(state).loadPending;
export const getError = (state: State): unknown => getGoalsStore(state).error;

export const getLastSyncDate = (state: State) => getGoalsStore(state).lastSyncDate;

export const getGoalByTagId = createSelector(
  getResourcesById,
  (_state: State, { id }: GoalTagProps) => id,
  (resources, tagIdToFind) => resources.find(({ tagId }) => tagId === tagIdToFind),
);

export const getGoalsSorted = createSelector(getResourcesById, (allGoals) =>
  allGoals.sort((a, b) => {
    const dateA = DateTime.fromISO(a.modifiedAt ?? '');
    const dateB = DateTime.fromISO(b.modifiedAt ?? '');
    if (a.modifiedAt) return dateA.toMillis() > dateB.toMillis() ? -1 : 1;
    return -1;
  }),
);
export const getGoalsSortedByDateThenCompleted = createSelector(getGoalsSorted, (sortedGoals) =>
  sortedGoals.sort((goal1, goal2) => {
    if (!goal1.completedOn && !goal2.completedOn) {
      return 0;
    }
    return !goal1.completedOn ? -1 : 1;
  }),
);
export const getCompletedGoalsById = createSelector(getGoalsSorted, (goalsById) =>
  goalsById.filter((goal) => goal.completedOn && goal.completedOn !== EPOCH_ISO),
);
export const getActiveGoalsById = createSelector(getGoalsSorted, (goalsById) =>
  goalsById.filter((goal) => !goal.completedOn || goal.completedOn === EPOCH_ISO),
);

// This selector will return all goal transactions matching id & month/year
export const getGoalTxnsForIdAndDate = createSelector(
  (state: State, props: GoalTxnDateProps): GoalTxnsById =>
    transactionsSelectors.getTransactionsByAccountId(state).get(props.acctId) ?? emptyGoalTransactions,
  (_state: State, props: GoalTxnDateProps) => props.month,
  (_state: State, props: GoalTxnDateProps) => props.year,
  (goalTxns, month, year): GoalTxnsById => {
    if (goalTxns.size > 0) {
      return goalTxns.filter((txn) => {
        // filter goal txns by year & month (passed as property to selector)
        const date = DateTime.fromISO(txn.postedOn ?? '');
        return date.month === month && date.year === year;
      });
    }
    return emptyGoalTransactions;
  },
);

const isInSameOrPreviousMonth = (selectedDate: DateTime | null | undefined, currentDate: DateTime): boolean => {
  if (!selectedDate) {
    return true;
  }
  const startOfTodayMonth = currentDate.startOf('month');
  return selectedDate >= startOfTodayMonth.minus({ months: 1 }) && selectedDate < startOfTodayMonth.plus({ months: 1 });
};

export const canContributeToGoal = createSelector(
  [
    (_state: State, date: DateTime | null | undefined) => date,
    (state: State) => featureFlagsSelectors.getFeatureFlag(state, 'contributeToGoalInUnlimitedPastMonths'),
    (state: State) => featureFlagsSelectors.getFeatureFlag(state, 'contributeToGoalOneMonthPast'),
    (state: State) => dateSelectors.getCurrentDate(state),
  ],
  (selectedDate, doNotLimitContributions, contributeToGoalOneMonthPast, currentDate) => {
    const currentMonth = currentDate.startOf('month');
    // If no date is supplied, we enable it as the addToGoal dialog simply uses the current date by default.
    if (!selectedDate) {
      return true;
    }
    const limitContributionDateRange = contributeToGoalOneMonthPast
      ? isInSameOrPreviousMonth(selectedDate, currentMonth)
      : currentMonth.month === selectedDate.month && currentMonth.year === selectedDate.year;

    return doNotLimitContributions ? currentMonth >= selectedDate : limitContributionDateRange;
  },
);

export const getGoalTotalWithoutSpentSoFar = createSelector(getGoalTxnsForIdAndDate, (goalTxns): number =>
  goalTxns.reduce(
    (sum, txn) =>
      sum +
      (txn.subtype === TransactionSubType.GoalStartingAmount || txn.subtype === TransactionSubType.GoalSpent
        ? 0
        : Number(txn.amount)),
    0,
  ),
);

export const getUniqueContributors = createSelector(
  (state: State, props: GoalAccountProps): GoalTxnsById | undefined =>
    props.accountId
      ? transactionsSelectors
          .getTransactionsByAccountId(state, { txnTypes: transactionsSelectors.TxnTypes.ALL })
          .get(props.accountId)
      : undefined,
  (goalTxns) => {
    const balanceMap = new Map<string, number>();
    if (goalTxns && goalTxns.size > 0) {
      const getBalance = (id: string) =>
        goalTxns
          .filter((txn) => txn.coa?.id === id)
          .map((txn) => Number(txn.amount))
          .reduce((sum, current) => numeral(sum).add(current).value() ?? 0, 0);

      goalTxns
        .map((txn) => txn.coa?.id)
        .toSet()
        .forEach((uid) => {
          if (!uid) return;
          const balance = getBalance(uid);
          if (balance > 0 || balance < 0) balanceMap.set(uid, balance);
          else balanceMap.delete(uid);
        });
    }
    return ImmutableMap<string, number>(balanceMap);
  },
);
// NOTE: isBankAccount returns active account only, when we conslidate accountsUtils, please make sure we filter out closed accounts here.
export const getPossibleContributionAccounts = createSelector(
  accountsSelectors.getAccountsById,
  (state: State) => featureFlagsSelectors.getFeatureFlag(state, 'investmentAccountGoalContributions'),
  (accountsById, investmentAccountGoalContributions): AccountsById =>
    accountsById == null
      ? ImmutableMap<string, accountsTypes.Account>()
      : accountsById.filter((account) =>
          investmentAccountGoalContributions
            ? accountsSelectors.isBankAccount(account) || accountsSelectors.isInvestmentAccount(account)
            : accountsSelectors.isBankAccount(account),
        ),
);

export const getGoalFundingAccountsWithBalances = createSelector(
  getPossibleContributionAccounts,
  balanceSelectors.getBalancesByAccountIds,
  (fundingAccounts, accountBalances) => {
    return fundingAccounts.map((account) => {
      const accountBalance = account.id ? accountBalances.get(account.id) : undefined;
      const { goalsBalance = 0, availableBalance = 0 } = accountBalance || {};
      return mkGoalFundingAccount({ ...account.toObject(), availableBalance, goalsBalance });
    });
  },
);

export const getAllUniqueContributorIds = createSelector(
  (state: State, props: GoalAccountProps): GoalTxnsById | undefined =>
    props.accountId
      ? transactionsSelectors
          .getTransactionsByAccountId(state, { txnTypes: transactionsSelectors.TxnTypes.ALL })
          .get(props.accountId)
      : undefined,
  (state: State) => getPossibleContributionAccounts(state),
  (goalTxns, fundingAccounts) => {
    const accountKeys = goalTxns?.keySeq().toArray();
    const matchingAccountsIds = accountKeys?.map((key) => {
      const matchingAccount = fundingAccounts?.find((account) => account?.id === goalTxns?.get(key)?.coa?.id);
      return matchingAccount?.id ?? null;
    });
    return [...new Set(matchingAccountsIds)];
  },
);

export const getGoalAccountTxns = createSelector(
  [
    (state: State) =>
      transactionsSelectors.getTransactionsByAccountId(state, { txnTypes: transactionsSelectors.TxnTypes.ALL }),
    accountsSelectors.getGoalAccountIds,
  ],
  (txnsByAccount, goalIds) => txnsByAccount.flatMap((txns, id) => (goalIds.has(id) ? txns : [])),
);
const emptyNumberMap = ImmutableMap<string, number>();

export const getAccountsBreakdown = createSelector([getLoadPending, getGoalAccountTxns], (loadPending, goalTxns) => {
  if (loadPending) return null;
  return ImmutableMap<string, ImmutableMapType<string, number>>()
    .withMutations((mutableParent) => {
      goalTxns.forEach((txn) => {
        const accountId = txn.coa?.id;
        const goalId = txn?.accountId;
        if (!goalId || !accountId || (txn.subtype !== 'GOAL_STARTING_AMOUNT' && !txn.transfer)) return;
        if (!mutableParent.has(accountId)) {
          mutableParent.set(accountId, ImmutableMap<string, number>());
        }
        mutableParent.update(accountId, (value: ImmutableMap<string, number> = emptyNumberMap) =>
          value.update(goalId, (goalValue = 0) => numberUtils.round(goalValue + txn.amount)),
        );
      });
      // prune zero-value goals from the account lists, and zero-value accounts from display
      mutableParent.map((goalMap) => goalMap.filter((goalContributionAmount) => goalContributionAmount));
    })
    .filter((goalMap) => goalMap?.size);
});

export const getGoalContributions = createSelector(
  (state: State, props: GoalAccountProps): GoalTxnsById | undefined =>
    props.accountId
      ? transactionsSelectors
          .getTransactionsByAccountId(state, { txnTypes: transactionsSelectors.TxnTypes.ALL })
          .get(props.accountId)
      : undefined,
  (goalTxns) => {
    let myGoalTxns: transactionsTypes.TransactionRecord[] = [];
    if (goalTxns) {
      myGoalTxns = goalTxns
        .toList()
        .sort((a, b) => ((a.postedOn ?? '') < (b.postedOn ?? '') ? 1 : -1))
        .toArray();
    }
    return myGoalTxns;
  },
);
